import { useRouter } from 'next/router';
import { stringify } from 'qs';
import { useCallback, useEffect, useRef } from 'react';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState, } from 'recoil';
import { getQueryString, getQueryStringValue, getWindowQueryString, } from '@zurich/common/types';
// replace the current query string with a new one
export function useSetQueryString() {
    const router = useRouter();
    return (queryString, options) => {
        const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryString}`;
        if (options?.replaceHistory) {
            void router.replace(url);
        }
        else {
            void router.push(url, undefined, { shallow: true });
        }
    };
}
/**
 * This global value is used to keep track of the query string outside the world of React and DOM.
 * Doing so ensures that we can instantly set and get the value without having to wait for re-renders
 * or DOM effects, avoiding race conditions.
 */
let currentQueryString = getWindowQueryString();
// set a certain key in the query string
export function useSetQueryStringValue() {
    const setQueryString = useSetQueryString();
    return (key, value, options) => {
        const values = getQueryString(currentQueryString);
        const newQueryString = stringify({ ...values, [key]: value }, { addQueryPrefix: true, encodeValuesOnly: true });
        currentQueryString = newQueryString;
        setQueryString(newQueryString, options);
    };
}
// remove a certain key from the query string
export function useRemoveQueryStringValue() {
    const setQueryString = useSetQueryString();
    return (key, options) => {
        const values = getQueryString(currentQueryString);
        delete values[key];
        const newQueryString = stringify(values, {
            addQueryPrefix: true,
            encodeValuesOnly: true,
        });
        currentQueryString = newQueryString;
        if (newQueryString.length > 0) {
            setQueryString(newQueryString, options);
        }
        else {
            // all values were deleted
            setQueryString('', options);
        }
    };
}
/**
 * Hook to get and set a query string value using next/router
 *
 * @param key The key to get the value for
 * @param initialValue The initial value to use if the key is not present in the query string. Defaults to an empty string.
 * @returns The value from the query string
 */
export function useNextAppQueryString({ key, initialValue = '', options, onChange, resetQueryParamsTo, }) {
    const queryStringValue = getQueryStringValue(key);
    const setQueryString = useSetQueryString();
    const setQueryStringValue = useSetQueryStringValue();
    const removeQueryStringValue = useRemoveQueryStringValue();
    const value = useRef(queryStringValue ?? initialValue);
    useEffect(() => {
        if (value.current !== queryStringValue) {
            value.current = queryStringValue ?? initialValue;
        }
    }, [queryStringValue, value, initialValue]);
    const onSetValue = useCallback((newValue) => {
        if (!newValue) {
            removeQueryStringValue(key);
            if (newValue === '') {
                onChange?.(newValue);
            }
        }
        else {
            setQueryStringValue(key, newValue, options);
            value.current = newValue;
            onChange?.(newValue);
        }
        if (resetQueryParamsTo?.length) {
            const queryParamObj = {};
            resetQueryParamsTo.forEach((queryParam) => {
                queryParamObj[queryParam] = getQueryStringValue(queryParam);
            });
            setQueryString(stringify(queryParamObj, {
                addQueryPrefix: true,
                encodeValuesOnly: true,
            }));
        }
    }, [
        resetQueryParamsTo,
        removeQueryStringValue,
        key,
        onChange,
        setQueryStringValue,
        options,
        setQueryString,
    ]);
    return [value.current, onSetValue];
}
// Need a Synced Version of the queryParams
export const nextQueryParamsAtom = atom({
    key: 'nextQueryParams',
    default: {},
});
let isQueryParamsListener = false;
export const removeStartAndEndingSlashes = (value) => {
    let ret = value;
    if (value.startsWith('/')) {
        ret = value.slice(1);
    }
    if (value.endsWith('/')) {
        ret = value.slice(0, -1);
    }
    return ret;
};
export const compareBaseUrls = (currentBaseUrl, baseUrlFromDefault) => {
    const preparedCurrentBaseUrl = removeStartAndEndingSlashes(currentBaseUrl);
    const preparedBaseUrlFromDefault = removeStartAndEndingSlashes(baseUrlFromDefault);
    return preparedCurrentBaseUrl === preparedBaseUrlFromDefault;
};
export function useSyncQueryParams(baseUrlToQueryParams) {
    const router = useRouter();
    const setQueryParams = useSetRecoilState(nextQueryParamsAtom);
    const setQueryParamsWithUrl = useSetNextQueryParams();
    const getDefaultQueryParamsByBaseUrl = () => {
        const currentBaseUrl = window.location.pathname.split('?')?.[0];
        if (!currentBaseUrl) {
            return {};
        }
        return (Object.entries(baseUrlToQueryParams).find(([key]) => compareBaseUrls(key, currentBaseUrl))?.[1] ?? {});
    };
    const updateQueryParams = useCallback((url, options) => {
        try {
            // reset query params on subsequent path change
            if (!options.initialRequest) {
                setQueryParams({});
            }
            // TODO: Only allow following urls. To allow for the entire app we need to refactor the useNextAppQueryString hook
            if (!url.includes('contacthistory') &&
                !url.includes('requests') &&
                !url.includes('evaluations') &&
                !url.includes('automation')) {
                return;
            }
            const urlObj = new URL(url, window.location.origin);
            // TODO: what if accidently get a query  params initialy that we cant changed in the ui - like TM Bearbeiter -> QueryByState=ASSIGNED
            const params = {};
            urlObj.searchParams.forEach((value, key) => {
                params[key] = value;
            });
            const defaultValues = getDefaultQueryParamsByBaseUrl();
            let defaultSet = false;
            for (const [key, value] of Object.entries(defaultValues)) {
                if (params[key] === undefined || params[key] === null) {
                    params[key] = value;
                    defaultSet = true;
                }
            }
            if (defaultSet) {
                setQueryParamsWithUrl(params);
            }
            else {
                setQueryParams(params);
            }
        }
        catch (e) {
            console.error('Invalid Url', e);
        }
    }, [setQueryParams]);
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        // Initial Synchronisierung
        updateQueryParams(window.location.href, { initialRequest: true });
        if (!isQueryParamsListener) {
            // Listener für Routenänderungen
            router.events.on('routeChangeComplete', (url, options) => updateQueryParams(url, options) // FIXME: for some reason this only works with this anonymous function
            );
            isQueryParamsListener = true;
        }
        // Cleanup on Unmount
        return () => {
            router.events.off('routeChangeComplete', (url, options) => updateQueryParams(url, options));
        };
    }, [router.events, updateQueryParams]);
}
export function useNextQueryParams() {
    return useRecoilValue(nextQueryParamsAtom);
}
export function useSetNextQueryParams() {
    const [params, setParamsState] = useRecoilState(nextQueryParamsAtom);
    return function setParams(newParams) {
        const updatedParams = { ...params, ...newParams };
        setParamsState(updatedParams);
        window.history.replaceState(null, '', `?${new URLSearchParams(updatedParams).toString()}`);
    };
}
export function useSetNextQueryParam(key) {
    const setParams = useSetNextQueryParams();
    return function setParam(value) {
        setParams({ [key]: value });
    };
}
export function useNextQueryParam(key, defaultValue = '') {
    const params = useNextQueryParams();
    return params[key] || defaultValue;
}
